import Titulo from '../../../components/tipografia/Titulo';
import LayoutUsuario from '../../../components/navegacao/LayoutUsuario';
import Descricao from '../../../components/tipografia/Descricao';
import HBox from '../../../components/layout/HBox';
import VBox from '../../../components/layout/VBox';
import Grid, { GridItem } from '../../../components/layout/Grid';
import CampoSeletor from '../../../components/Formulario/CampoSeletor';
import CampoData from '../../../components/Formulario/CampoData';
import Botao from '../../../components/Botao';
import Skeleton from '../../../components/layout/Skeleton';
import DataTable, { DataTableHeader } from '../../../components/layout/DataTable';
import CardRelatorio from '../CardRelatorio';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import {
  Bar,
  ComposedChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  format,
  differenceInDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from 'date-fns';
import {
  PERIODO_RELATIVO_OPTIONS,
  resolvePeriodoRelativo,
  PeriodoRelativoToken,
} from '../util';
import { currencyFormat } from '../financeiro/util';

// Igual ao ajuste feito em relatorios/financeiro: fluxo de caixa também lida
// com lançamentos futuros (contas a vencer, previstas), então o período
// precisa ir até o fim do intervalo, não até hoje.
const resolvePeriodoFluxoCaixa = (
  periodoToken: PeriodoRelativoToken,
  customInicio: string,
  customFim: string,
) => {
  const hoje = new Date();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

  switch (periodoToken) {
    case 'semana':
      return {
        dataInicio: fmt(startOfWeek(hoje, { weekStartsOn: 0 })),
        dataFim: fmt(endOfWeek(hoje, { weekStartsOn: 0 })),
      };
    case 'mes':
      return { dataInicio: fmt(startOfMonth(hoje)), dataFim: fmt(endOfMonth(hoje)) };
    case 'ano':
      return { dataInicio: fmt(startOfYear(hoje)), dataFim: fmt(endOfYear(hoje)) };
    default:
      return resolvePeriodoRelativo(periodoToken, customInicio, customFim);
  }
};

const parseDate = (v: string) =>
  new Date(v.substring(0, 10) + 'T00:00:00.000').toLocaleDateString();

const headers: DataTableHeader[] = [
  {
    label: 'Período',
    render: (row: any) => parseDate(row.periodo),
  },
  {
    label: 'Entradas realizadas',
    alignRight: true,
    render: (row: any) => currencyFormat.format(row.entradas_realizadas),
  },
  {
    label: 'Entradas previstas',
    alignRight: true,
    render: (row: any) => currencyFormat.format(row.entradas_previstas),
  },
  {
    label: 'Saídas realizadas',
    alignRight: true,
    render: (row: any) => currencyFormat.format(row.saidas_realizadas),
  },
  {
    label: 'Saídas previstas',
    alignRight: true,
    render: (row: any) => currencyFormat.format(row.saidas_previstas),
  },
  {
    label: 'Saldo do período',
    alignRight: true,
    render: (row: any) => currencyFormat.format(row.saldo_periodo_realizado),
  },
  {
    label: 'Saldo acumulado (realizado)',
    alignRight: true,
    render: (row: any) => currencyFormat.format(row.saldo_acumulado_realizado),
  },
  {
    label: 'Saldo acumulado (projetado)',
    alignRight: true,
    render: (row: any) => currencyFormat.format(row.saldo_acumulado_projetado),
  },
];

const FluxoCaixaPage = () => {
  const [periodoToken, setPeriodoToken] = useState<PeriodoRelativoToken>('mes');
  const [customInicio, setCustomInicio] = useState<string>('');
  const [customFim, setCustomFim] = useState<string>('');

  const { dataInicio, dataFim } = resolvePeriodoFluxoCaixa(
    periodoToken,
    customInicio,
    customFim,
  );

  const calcBestAgrupamento = () => {
    const days = differenceInDays(dataFim, dataInicio);
    if (days <= 7) return 'dia';
    if (days <= 62) return 'semana';
    return 'mes';
  };

  const [agrupamento, setAgrupamento] = useState<'dia' | 'semana' | 'mes'>(
    calcBestAgrupamento,
  );

  useEffect(() => {
    setAgrupamento(calcBestAgrupamento());
  }, [dataInicio, dataFim]);

  const q = useQuery(
    ['fluxo_caixa', dataInicio, dataFim, agrupamento],
    async () => {
      const res = await axios.get('/api/relatorios/fluxo_caixa', {
        params: { data_inicio: dataInicio, data_fim: dataFim, agrupamento },
      });
      return res.data;
    },
    { refetchOnWindowFocus: false },
  );

  const rows = q.data?.result ?? [];

  return (
    <LayoutUsuario>
      <VBox gap="8px">
        <HBox>
          <VBox style={{ flex: 1 }}>
            <Titulo>Fluxo de caixa</Titulo>
            <Descricao>
              Saldo acumulado ao longo do tempo, a partir do último ajuste de
              saldo, considerando o que já foi pago (realizado) e o que ainda
              está previsto.
            </Descricao>
          </VBox>
        </HBox>

        <Grid>
          <GridItem sm={12} md={6} lg={4}>
            <HBox>
              <CampoSeletor
                noSearch
                options={PERIODO_RELATIVO_OPTIONS as unknown as { id: string; label: string }[]}
                placeholder="Filtrar por período"
                value={periodoToken}
                onChange={value => setPeriodoToken(value as PeriodoRelativoToken)}
                style={{ width: '100%', flex: 1 }}
              />
              {periodoToken === 'custom' && (
                <>
                  <CampoData
                    placeholder="Data inicial"
                    value={customInicio}
                    onChange={setCustomInicio}
                  />
                  <CampoData
                    placeholder="Data final"
                    value={customFim}
                    onChange={setCustomFim}
                  />
                </>
              )}
            </HBox>
          </GridItem>
        </Grid>

        <CardRelatorio titulo="Saldo acumulado" style={{ minHeight: '460px' }}>
          <HBox>
            <Botao
              variant={agrupamento === 'dia' ? 'outline-primary' : 'outline-secondary'}
              onClick={() => setAgrupamento('dia')}
            >
              Dia
            </Botao>
            <Botao
              variant={agrupamento === 'semana' ? 'outline-primary' : 'outline-secondary'}
              onClick={() => setAgrupamento('semana')}
            >
              Semana
            </Botao>
            <Botao
              variant={agrupamento === 'mes' ? 'outline-primary' : 'outline-secondary'}
              onClick={() => setAgrupamento('mes')}
            >
              Mês
            </Botao>
          </HBox>
          {q.isFetching ? (
            <Skeleton format="box" />
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={rows} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodo" tickFormatter={v => parseDate(v)} />
                <YAxis tickFormatter={v => currencyFormat.format(v)} width={90} />
                <Tooltip
                  labelFormatter={v => parseDate(v as string)}
                  formatter={(v: any) => currencyFormat.format(+v)}
                />
                <Legend />
                <Bar dataKey="entradas_realizadas" name="Entradas" fill="#22c55e" />
                <Bar dataKey="saidas_realizadas" name="Saídas" fill="#7339ac" />
                <Line
                  type="monotone"
                  dataKey="saldo_acumulado_realizado"
                  name="Saldo acumulado (realizado)"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="saldo_acumulado_projetado"
                  name="Saldo acumulado (projetado)"
                  stroke="#2563eb"
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardRelatorio>

        <DataTable
          isLoading={q.isFetching}
          cabecalhos={headers}
          linhas={rows}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default FluxoCaixaPage;
